import type { TradingAccountSnapshot, TradingAdapterCapabilities, TradingQuote, TradingRiskProfileDto, TradingSymbol, TradingRuntimeMetadata } from '@/api/trading';
import { Table } from '@/components/ui/Table';

export interface BrokerInputs {
  capturedUtc?: string;
  quote?: TradingQuote | null;
  symbol?: TradingSymbol | null;
  account?: TradingAccountSnapshot | null;
  capabilities?: TradingAdapterCapabilities | null;
  risk?: TradingRiskProfileDto | null;
  agentVersion?: string | null;
  runtimeMetadata?: TradingRuntimeMetadata | null;
  riskDecision?: { profileConfigured?: boolean | null } | null;
}

export function BrokerInputComparison({ captured, current, connected = false }: { captured: BrokerInputs | null; current: BrokerInputs; connected?: boolean }) {
  const fields: [string, (input: BrokerInputs) => unknown][] = [
    ['Account', x=>x.account?.accountId], ['Environment', x=>x.account?.environment],
    ['Position accounting', x=>x.account?.positionMode], ['Currency', x=>x.account?.currency],
    ['Account snapshot UTC', x=>x.account?.asOfUtc], ['Balance', x=>x.account?.balance],
    ['Equity', x=>x.account?.equity], ['Margin', x=>x.account?.margin], ['Free margin', x=>x.account?.freeMargin],
    ['Quote symbol', x=>x.quote?.symbol], ['Quote UTC', x=>x.quote?.timeUtc], ['Bid', x=>x.quote?.bid], ['Ask', x=>x.quote?.ask],
    ['Rule symbol', x=>x.symbol?.symbol], ['Volume unit', x=>x.symbol?.volumeUnit],
    ['Minimum volume', x=>x.symbol?.minimumVolume], ['Maximum volume', x=>x.symbol?.maximumVolume],
    ['Volume step', x=>x.symbol?.volumeStep], ['Price increment', x=>x.symbol?.tickSize],
    ['Supported order types', x=>x.symbol?.supportedOrderTypes],
    ['Trade mode', x=>x.symbol?.executionConstraints?.tradeMode],
    ['Stop loss supported', x=>x.symbol?.executionConstraints?.supportsStopLoss],
    ['Take profit supported', x=>x.symbol?.executionConstraints?.supportsTakeProfit],
    ['Minimum protection distance', x=>x.symbol?.executionConstraints?.minimumStopDistance],
    ['Freeze distance', x=>x.symbol?.executionConstraints?.freezeDistance],
    ['Market time in force', x=>x.symbol?.executionConstraints?.marketTimeInForce],
    ['Pending time in force', x=>x.symbol?.executionConstraints?.pendingTimeInForce],
    ['Specified expiration supported', x=>x.symbol?.executionConstraints?.supportsSpecifiedExpiration],
    ['Broker check supported', x=>x.capabilities?.supportsBrokerCheck],
    ['Account risk profile configured', x=>x.risk ? true : x.riskDecision?.profileConfigured],
    ['Maximum quote age (seconds)', x=>x.risk?.maxQuoteAgeSeconds],
    ['Maximum open positions', x=>x.risk?.maxOpenPositions],
    ['Maximum orders per minute', x=>x.risk?.maxOrdersPerMinute],
    ['Maximum daily realized loss', x=>x.risk?.maxDailyRealizedLoss],
    ['Maximum market slippage (bps)', x=>x.risk?.maxMarketSlippageBps],
    ['Symbol risk limits', x=>x.risk?.symbols.find(s=>s.symbol===x.symbol?.symbol)],
    ['Gateway version', x=>x.agentVersion],
    ['Gateway build', x=>x.runtimeMetadata?.gatewayVersion],
    ['Advertised EA version', x=>x.runtimeMetadata?.eaVersion],
    ['Terminal build', x=>x.runtimeMetadata?.terminalBuild],
    ['Broker time zone', x=>x.runtimeMetadata?.brokerTimeZoneId],
    ['Broker UTC offset (minutes)', x=>x.runtimeMetadata?.brokerUtcOffsetMinutes],
    ['Configured clock shift (minutes)', x=>x.runtimeMetadata?.brokerTimeZoneShiftMinutes],
  ];
  const display = (value: unknown, missing: string) => value == null ? missing :
    typeof value === 'object' ? JSON.stringify(value) : String(value);
  return <section aria-label="Captured and current broker inputs"><h3>Captured and current broker inputs</h3>
    <p>{connected?'Gateway connected.':'Gateway offline: current values are the last recorded values, not a fresh broker snapshot.'} Quote freshness: {!current.quote?'unavailable':Date.now()-Date.parse(current.quote.timeUtc)>(current.risk?.maxQuoteAgeSeconds??30)*1000?'stale':'within configured age limit'}.</p>
    <p>{captured ? `Recorded inputs: ${captured.capturedUtc??'capture time not recorded'}.` : 'Original broker inputs were not captured for this command; this comparison is incomplete.'} Current values refresh from the selected connection. Missing values are not proof of a match. Validate and Submit recheck current conditions.</p>
    <Table headers={['Input','Captured','Current','Comparison']}>
      {fields.map(([label,read])=>{const before=captured?read(captured):undefined, after=read(current);
        return <tr key={label}><td>{label}</td><td>{display(before,'Not captured')}</td><td>{display(after,'Unavailable')}</td><td>{before==null||after==null?'Missing evidence':JSON.stringify(before)===JSON.stringify(after)?'Match':'Difference'}</td></tr>;})}
    </Table>
  </section>;
}
